import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

const PricingContainer = styled.div`
  min-height: 100vh;
  padding: 60px 20px;
  background: #335da9;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const FuturisticBox = styled(motion.div)`
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  padding: 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  position: relative;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
  }
`;

const PricingTitle = styled.h1`
  font-size: 3.2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 16px;
  position: relative;
  display: inline-block;
  width: 100%;
  text-align: center;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #8bc34a, transparent);
  }

  @media (max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 40px;
  margin-top: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  font-weight: 600;
  color: #8bc34a;
  margin-bottom: 16px;
  margin-top: ${(props) => (props.$first ? "0" : "32px")};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled(motion.li)`
  margin-bottom: 12px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 10px;
  border-left: 3px solid #8bc34a;
  font-size: 1rem;
  line-height: 1.5;
  transition: transform 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    transform: translateX(5px);
  }
`;

const Bullet = styled.span`
  color: #8bc34a;
  margin-right: 10px;
  font-size: 1.2em;
`;

const HighlightText = styled.span`
  color: #8bc34a;
  font-weight: 500;
`;

const Disclaimer = styled.p`
  text-align: center;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.05, duration: 0.4 },
  }),
};

const sections = [
  {
    title: "Getting Started",
    items: [
      "Free to install on Shopify",
      "No setup fees or onboarding charges",
      "No minimum order volume required",
    ],
  },
  {
    title: "Order Charges",
    items: [
      "Cost of goods is charged per order from your in-app wallet",
      "Product prices are set by suppliers and visible before you place an order",
      "Shipping costs are calculated based on weight and destination",
    ],
  },
  {
    title: "Additional Fees",
    items: [
      { label: "COD Commission", desc: "Applied on Cash-on-Delivery orders" },
      {
        label: "RTO Penalty",
        desc: "Charged if the shipment is returned to origin",
      },
      {
        label: "Return Penalty",
        desc: "Charged on customer-initiated returns",
      },
    ],
  },
  {
    title: "Payouts",
    items: [
      "Payouts are processed within 4 business days of delivery",
      "All transactions are managed via your in-app wallet",
      "Withdraw to your bank account anytime",
    ],
  },
];

const Pricing = () => {
  let itemIndex = 0;

  return (
    <PricingContainer>
      <FuturisticBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <PricingTitle>Pricing &amp; Charges</PricingTitle>
        <Subtitle>
          Transparent pricing with no hidden fees. You only pay for what you use.
        </Subtitle>

        {sections.map((section, sIdx) => (
          <div key={section.title}>
            <SectionTitle $first={sIdx === 0}>{section.title}</SectionTitle>
            <List>
              {section.items.map((item) => {
                const idx = itemIndex++;
                const isObject = typeof item === "object";
                return (
                  <ListItem
                    key={idx}
                    custom={idx}
                    initial="hidden"
                    animate="visible"
                    variants={itemVariants}
                  >
                    <Bullet>•</Bullet>
                    {isObject ? (
                      <>
                        <HighlightText>{item.label}:</HighlightText>{" "}
                        {item.desc}
                      </>
                    ) : (
                      item
                    )}
                  </ListItem>
                );
              })}
            </List>
          </div>
        ))}

        <Disclaimer>
          Rates may vary based on your account configuration. For detailed
          pricing specific to your store, please check your seller dashboard.
        </Disclaimer>
      </FuturisticBox>
    </PricingContainer>
  );
};

export default Pricing;
